import { inject, Injectable } from '@angular/core';
import { cluster as radashCluster } from 'radash';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { SESSION_STORE_KEY } from '@connect-services/client/resolvers/session.resolver';
import {
    EditModeStateService,
    EditState,
    EntityEdit,
    LayerSnapshot,
} from '@connect-services/client/services/edit-mode-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import {
    EditConflicts,
    EditConflictsDeletedEntitiesInner,
    EditModeUpsertResult,
    EntityState,
    LayersService,
} from '@connect-services/src/sdk/api';

const MAX_SIZE_ENTITY_STATE: number = 300;

@Injectable({
    providedIn: 'root',
})
export class EditModeSaveService {
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);

    public async saveChanges(params: {
        editConflicts?: EditConflicts;
        keepEditSessionOpen?: boolean;
        layerIds?: string[];
    }): Promise<EditModeUpsertResult[]> {
        const { layerIds, editConflicts, keepEditSessionOpen } = params;

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            throw new Error('Project not found.');
        }

        const projectState = this.prepareProjectStateForSave(layerIds, editConflicts);
        if (!Object.keys(projectState).length) {
            return [];
        }

        const batches = await this.buildEntityStateBatches(projectState, projectId);
        const editSessionInfo = this.projectEditModeService.getEditSession();
        const sessionId = sessionStorage.getItem(SESSION_STORE_KEY);

        if (!editSessionInfo || !sessionId) {
            throw new Error('[EditModeSaveService] Missing session identifiers');
        }

        const editResults = await Promise.all(
            batches.map((batch: EntityState[]) =>
                firstValueFrom(
                    this.layersService.layerControllerPostEntitiesChanges({
                        entityChangesDto: {
                            editSessionId: editSessionInfo.id,
                            projectId,
                            sessionId,
                            state: batch,
                        },
                    }),
                ),
            ),
        );

        layerIds?.length
            ? await this.editModeStateService.clearLayerStateAndEvents(projectId, layerIds)
            : this.editModeStateService.clearEditModeState(projectId);

        if (keepEditSessionOpen) {
            this.projectEditModeService.refreshEditSessionStart();
        }

        return editResults.flat();
    }

    private async buildEntityStateBatches(projectState: EditState, projectId: string): Promise<EntityState[][]> {
        const layerDepositsById = await this.getLayerDepositsById(projectState);

        const flatStateOperations: EntityState[] = Object.entries(projectState).flatMap(
            ([layerId, layerSnapshot]: [string, LayerSnapshot]) => {
                return Object.entries(layerSnapshot).map(([entityId, snapshotItem]: [string, EntityEdit]) => {
                    const resolvedLayerId = layerId !== snapshotItem.tableName ? layerId : undefined;
                    const depositId =
                        snapshotItem.properties?.['deposit_id'] ??
                        this.editModeStateService.buildBefore(projectId, entityId)['deposit_id'] ??
                        (resolvedLayerId ? layerDepositsById.get(resolvedLayerId) : undefined);

                    return {
                        ...snapshotItem,
                        depositId,
                        entityId: entityId,
                        ...(resolvedLayerId && { layerId: resolvedLayerId }),
                    };
                });
            },
        );

        const sortedOperations = flatStateOperations
            .filter((operation: EntityState) => operation.saveOrder !== -1)
            .sort((operationA, operationB) => {
                return (
                    (operationA.saveOrder ?? Number.MAX_SAFE_INTEGER) -
                    (operationB.saveOrder ?? Number.MAX_SAFE_INTEGER)
                );
            });

        return radashCluster(sortedOperations, MAX_SIZE_ENTITY_STATE);
    }

    private getExcludedEntityIds(editConflicts: EditConflicts | undefined): string[] {
        if (!editConflicts) {
            return [];
        }

        return editConflicts.deletedEntities
            .map((deletedEntitiesInfo: EditConflictsDeletedEntitiesInner) => deletedEntitiesInfo.entityId)
            .filter((entityId): entityId is string => !!entityId);
    }

    private getFilteredProjectState(
        projectState: EditState,
        excludedEntityIds: string[],
        excludedLayerIds: string[],
    ): EditState {
        const layerIds = Object.keys(projectState).filter((layerId) => !excludedLayerIds.includes(layerId));

        return layerIds.reduce((acc: EditState, layerId: string) => {
            const layerSnapshot: LayerSnapshot = projectState[layerId];
            const modifiedEntityIds: string[] = Object.keys(layerSnapshot);
            const finalLayerSnapshot: LayerSnapshot = {};

            for (const entityId of modifiedEntityIds) {
                if (excludedEntityIds.includes(entityId)) {
                    continue;
                }

                finalLayerSnapshot[entityId] = layerSnapshot[entityId];
            }

            acc[layerId] = finalLayerSnapshot;

            return acc;
        }, {});
    }

    private async getLayerDepositsById(projectState: EditState): Promise<Map<string, string | undefined>> {
        const layerIds = Object.keys(projectState).filter((layerId: string) => {
            const layerSnapshot = projectState[layerId];

            return Object.values(layerSnapshot).some((snapshotItem: EntityEdit) => layerId !== snapshotItem.tableName);
        });

        const deposits = await Promise.all(
            layerIds.map(async (layerId: string): Promise<[string, string | undefined]> => {
                try {
                    const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));

                    return [layerId, layer.deposit ?? undefined];
                } catch {
                    return [layerId, undefined];
                }
            }),
        );

        return new Map(deposits);
    }

    private prepareProjectStateForSave(layerIds?: string[], editConflicts?: EditConflicts): EditState {
        const projectState: EditState = this.editModeStateService.getProjectState(layerIds);

        if (!editConflicts) {
            return projectState;
        }

        const excludedEntityIds = this.getExcludedEntityIds(editConflicts);
        const filteredProjectState: EditState = this.getFilteredProjectState(
            projectState,
            excludedEntityIds,
            editConflicts.layerIdsWithoutDeposit ?? [],
        );

        return Object.fromEntries(
            Object.entries(filteredProjectState).filter(([, snapshot]) => snapshot && Object.keys(snapshot).length),
        );
    }
}
